import { useCombinedPdfGeneration } from 'hooks/useCombinedPdfGeneration'
import React, { FC, useEffect, useState, useRef, MouseEventHandler } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { EuiEmptyPrompt, EuiLoadingContent, EuiPage, EuiPageBody, EuiPanel, EuiSpacer, EuiTabbedContent, EuiFlexGroup, EuiFlexItem, EuiButtonIcon, EuiButtonEmpty } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, MasterCampaignStub } from 'api/interfaces/MasterCampaignStub'
import { Persona } from 'api/interfaces/Persona'
import { useGetBillboardsByZipCodesQuery } from 'api/rtkQueryApi/platform/billboardApi'
import { useCreateMasterCampaignPlanPublicIdMutation, useGetMasterCampaignPlanExecutiveSummarySectionQuery, useGetMasterCampaignPlanMasterCampaignStubQuery, useGetMasterCampaignPlanQuery, useMasterCampaignPlanRemovePersonaMutation, useMasterCampaignPlanResetAllMutation, useMasterCampaignStubResetMutation, useRemoveMasterCampaignStubCampaignBuilderMutation, useUpdateClientPreviewTabsMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import config from 'app/config'
import { RootState } from 'app/rootReducer'
import MultiTabPdfProgressModal from 'components/MultiTabPdfProgressModal'
import EditCampaignFlyout, { EditCampaignFlyoutMode } from 'features/planner/components/EditCampaignFlyout'
import { EditFlyout, EditType } from 'features/planner/components/EditFlyout'
import { STAFF_PDF_CENSUS_UNAVAILABLE_TOOLTIP, useHouseholdCensusData } from 'features/planner/hooks/useHouseholdCensusData'
import { ConfirmTotalResetModal } from 'features/planner/old-pages/MasterCampaignPlanPage/components/ConfirmTotalResetModal'
import { CampaignSpecificTargetedHouseholdMapRef, CampaignSpecificBillboardMapRef } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/CampaignSpecificMaps'
import { useBillboardZipCodes, useHouseholdZipCodes } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/useProjectionsUtils'
import { resetEditMode } from 'features/planner/pages/MasterCampaignPlanPage/masterCampaignPlanSlice'
import { selectMultiTabPdfGenerationState } from 'features/planner/pages/MasterCampaignPlanPage/pdfViewSlice'

import './MasterCampaignPlanPage.scss'
import ClientPreviewTabsFlyout, { ClientPreviewTabsSettings } from './components/ClientPreviewTabsFlyout'
import EditMarketAnalysisFlyout from './components/EditMarketAnalysisFlyout'
import ExecutiveSummary from './components/ExecutiveSummary/ExecutiveSummary'
import MarketAnalysis from './components/MarketAnalysis'
import MasterCampaignPlanHeader from './components/MasterCampaignPlanHeader'
import MediaPlan, { MediaPlanRef } from './components/MediaPlan'
import PageContent from './components/PageContent'
import EditPersonaFlyout, { EditPersonaFlyoutMode } from './components/Personas/EditPersonaFlyout'
import Personas from './components/Personas/Personas'
import ReorderPersonasFlyout from './components/Personas/ReorderPersonasFlyout'
import Projections from './components/Projections/Projections'

export interface MasterCampaignPlanPageProps {}

interface MasterCampaignPlanPageParams {
  masterCampaignPlanId: string
}

interface IEditCampaignFlyoutOpen {
  mode: EditCampaignFlyoutMode
  selectedCampaignBuilderId?: string
}

interface IEditPersonaFlyoutOpen {
  mode: EditPersonaFlyoutMode
  selectedPersona?: Persona
  initialData?: Persona
}

const MasterCampaignPlanPage: FC<MasterCampaignPlanPageProps> = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)
  const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
  const { isStaffPdf, isGenerating: isGeneratingAllPdfs } = useSelector((state: RootState) => selectMultiTabPdfGenerationState(state, masterCampaignPlanId))

  const [editFlyoutOpen, setEditFlyoutOpen] = useState(false)
  const [editFlyoutType, setEditFlyoutType] = useState<EditType | null>(null)
  const [editCampaignFlyoutOpen, setEditCampaignFlyoutOpen] = useState<IEditCampaignFlyoutOpen | null>(null)
  const [editPersonaFlyoutOpen, setEditPersonaFlyoutOpen] = useState<IEditPersonaFlyoutOpen | null>(null)
  const [isReorderPersonasFlyoutOpen, setIsReorderPersonasFlyoutOpen] = useState(false)
  const [isEditMarketAnalysisFlyoutOpen, setIsEditMarketAnalysisFlyoutOpen] = useState(false)
  const [isClientPreviewTabsFlyoutOpen, setIsClientPreviewTabsFlyoutOpen] = useState(false)
  const [confirmTotalResetModalVisible, setConfirmTotalResetModalVisible] = useState(false)
  const [selectedTab, setSelectedTab] = useState('executive-summary')
  const [showProgressModal, setShowProgressModal] = useState(false)

  const ganttChartRef = useRef<HTMLDivElement>(null)
  const marketAnalysisPrintRef = useRef<HTMLDivElement>(null)
  const deliveryMetricsRef = useRef<HTMLDivElement>(null)
  const connectedTvRef = useRef<HTMLDivElement>(null)
  const internetDisplayRef = useRef<HTMLDivElement>(null)
  const targetedHouseholdsRef = useRef<HTMLDivElement>(null)
  const billboardLocationsRef = useRef<HTMLDivElement>(null)
  const targetedHouseholdMapRef = useRef<any>(null)
  const billboardMapRef = useRef<any>(null)
  const mediaplanRef = useRef<MediaPlanRef>(null)

  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching, isLoading: isMasterCampaignPlanLoading, error: masterCampaignPlanError } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId }, { skip: !masterCampaignPlanId })
  const { data: masterCampaignStub, isFetching: isMasterCampaignStubFetching, isLoading: isMasterCampaignStubLoading, error: masterCampaignStubError } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlanId }, { skip: !masterCampaignPlanId })
  const { data: executiveSummarySection, isFetching: isExecutiveSummarySectionFetching, isLoading: isExecutiveSummarySectionLoading, error: executiveSummarySectionError } = useGetMasterCampaignPlanExecutiveSummarySectionQuery({ masterCampaignPlanId: masterCampaignPlanId }, { skip: !masterCampaignPlanId })

  const billboardZipCodes = useBillboardZipCodes(masterCampaignStub)
  const householdZipCodes = useHouseholdZipCodes(masterCampaignStub)

  useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  const { timedOutWithoutData: censusDataTimedOut } = useHouseholdCensusData(householdZipCodes)
  const staffPdfDisabledReason = censusDataTimedOut ? STAFF_PDF_CENSUS_UNAVAILABLE_TOOLTIP : undefined

  const [resetMasterCampaignPlanMutation, { isLoading: isResettingMasterCampaignPlan }] = useMasterCampaignPlanResetAllMutation()
  const [resetMasterCampaignStubMutation, { isLoading: isResettingMasterCampaignStub }] = useMasterCampaignStubResetMutation()
  const [removeMasterCampaignStubCampaignBuilderMutation, { isLoading: isRemovingCampaign }] = useRemoveMasterCampaignStubCampaignBuilderMutation()
  const [removePersonaMutation, { isLoading: isRemovingPersona }] = useMasterCampaignPlanRemovePersonaMutation()
  const [createMasterCampaignPlanPublicId, { isLoading: isCreatingPublicId }] = useCreateMasterCampaignPlanPublicIdMutation()
  const [updateClientPreviewTabsMutation, { isLoading: isUpdatingClientPreviewTabs }] = useUpdateClientPreviewTabsMutation()

  const tabRefs = {
    ganttChartRef,
    marketAnalysisPrintRef,
    projectionRefs: {
      deliveryMetricsRef,
      connectedTvRef,
      internetDisplayRef,
      targetedHouseholdsRef,
      billboardLocationsRef
    },
    projectionMapRefs: {
      targetedHouseholdMapRef,
      billboardMapRef
    },
    mediaplanRef
  }

  const combinedPdfHook = useCombinedPdfGeneration(
    masterCampaignPlan && masterCampaignStub
      ? {
          masterCampaignPlan,
          masterCampaignStub,
          onTabChange: setSelectedTab,
          tabRefs
        }
      : {
          masterCampaignPlan: {} as MasterCampaignPlan,
          masterCampaignStub: {} as MasterCampaignStub,
          onTabChange: setSelectedTab,
          tabRefs
        }
  )

  const { progress: pdfProgress, error: pdfError, generateAllClientPdfs, clearError: clearPdfError } = combinedPdfHook

  useEffect(() => {
    if (!currentAccount || !masterCampaignPlan) {
      return
    }

    if (currentAccount?.id !== masterCampaignPlan?.accountId) {
      history.push('/ai')
    }
  }, [currentAccount, masterCampaignPlan, history])

  useEffect(() => {
    return () => {
      dispatch(resetEditMode())
    }
  }, [dispatch])

  const onEditFlyoutClose = () => {
    setEditFlyoutOpen(false)
    setEditFlyoutType(null)
  }

  const onEditCampaignFlyoutClose = () => {
    setEditCampaignFlyoutOpen(null)
  }

  const onEditPersonaFlyoutClose = () => {
    setEditPersonaFlyoutOpen(null)
  }

  const onEditSectionClick = (editType: EditType) => {
    setEditFlyoutOpen(true)
    setEditFlyoutType(editType)
  }

  const onEditCampaignClick = (builder: MasterCampaignPlanCampaignBuilder) => {
    setEditCampaignFlyoutOpen({ mode: EditCampaignFlyoutMode.Edit, selectedCampaignBuilderId: builder.id })
  }

  const onAddCampaignClick = () => {
    setEditCampaignFlyoutOpen({ mode: EditCampaignFlyoutMode.Add })
  }

  const onEditPersonaClick = (persona: Persona) => {
    setEditPersonaFlyoutOpen({ mode: EditPersonaFlyoutMode.Edit, selectedPersona: persona })
  }

  const onAddPersonaClick = () => {
    setEditPersonaFlyoutOpen({ mode: EditPersonaFlyoutMode.Add })
  }

  const onReorderPersonasClick = () => {
    setIsReorderPersonasFlyoutOpen(true)
  }

  const onReorderPersonasFlyoutClose = () => {
    setIsReorderPersonasFlyoutOpen(false)
  }

  const onEditMarketAnalysisClick = () => {
    setIsEditMarketAnalysisFlyoutOpen(true)
  }

  const onEditMarketAnalysisFlyoutClose = () => {
    setIsEditMarketAnalysisFlyoutOpen(false)
  }

  const onDeletePersonaClick = async (personaToDelete: Persona): Promise<void> => {
    if (!masterCampaignPlan?.id || !currentOrganization?.id || !currentAccount?.id) {
      return
    }

    await removePersonaMutation({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      masterCampaignPlanId: masterCampaignPlan.id,
      personaName: personaToDelete.name,
      personaRank: personaToDelete.rank
    })
  }

  const onRemoveCampaignClick = async (builder: MasterCampaignPlanCampaignBuilder) => {
    if (!masterCampaignPlan?.id || !masterCampaignStub?.id || !builder.id) {
      return
    }

    await removeMasterCampaignStubCampaignBuilderMutation({
      masterCampaignPlanId: masterCampaignPlan.id,
      masterCampaignStubId: masterCampaignStub.id,
      masterCampaignStubCampaignBuilderId: builder.id
    })
  }

  const onResetClick = () => {
    setConfirmTotalResetModalVisible(true)
  }

  const onEditNameClick = () => {
    setEditFlyoutOpen(true)
    setEditFlyoutType(EditType.Name)
  }

  const onClientPreviewLinkClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!masterCampaignPlan?.id) {
      return
    }

    let publicId = masterCampaignPlan.publicId

    if (!publicId) {
      const updatedPlan = await createMasterCampaignPlanPublicId({
        masterCampaignPlanId: masterCampaignPlan.id
      }).unwrap()

      publicId = updatedPlan.publicId
    }

    if (publicId) {
      window.open(`${config.campaignPreviewUrl}${publicId}`, '_blank')
    }
  }

  const onAllClientPdfsClick = async () => {
    if (!masterCampaignPlan || !masterCampaignStub || isGeneratingAllPdfs) return

    setShowProgressModal(true)
    try {
      await generateAllClientPdfs(false)
    } catch (error) {
      console.error('Failed to generate all client PDFs:', error)
    }
  }

  const onAllStaffPdfsClick = async () => {
    if (!masterCampaignPlan || !masterCampaignStub || isGeneratingAllPdfs || staffPdfDisabledReason) return

    setShowProgressModal(true)
    try {
      await generateAllClientPdfs(true)
    } catch (error) {
      console.error('Failed to generate all client PDFs:', error)
    }
  }

  const onCloseProgressModal = () => {
    setShowProgressModal(false)
    clearPdfError()
  }

  const onRetryPdfGeneration = () => {
    clearPdfError()

    if (isStaffPdf) {
      onAllStaffPdfsClick()
    } else {
      onAllClientPdfsClick()
    }
  }

  const handleSaveClientPreviewTabs = async (settings: ClientPreviewTabsSettings) => {
    if (!masterCampaignPlan?.id) {
      return
    }

    await updateClientPreviewTabsMutation({
      masterCampaignPlanId: masterCampaignPlan.id,
      ...settings
    })

    setIsClientPreviewTabsFlyoutOpen(false)
  }

  if (isMasterCampaignPlanLoading || isMasterCampaignStubLoading || isExecutiveSummarySectionLoading || (!masterCampaignPlan && isMasterCampaignPlanFetching) || (!masterCampaignStub && isMasterCampaignStubFetching) || (!executiveSummarySection && isExecutiveSummarySectionFetching)) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiLoadingContent lines={10} />
        </EuiPageBody>
      </EuiPage>
    )
  }

  if (masterCampaignPlanError || !masterCampaignPlan || masterCampaignStubError || !masterCampaignStub || executiveSummarySectionError) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiEmptyPrompt title={<h2>Plan not found</h2>} body="The master campaign plan you're looking for doesn't exist or you don't have permission to view it." />
        </EuiPageBody>
      </EuiPage>
    )
  }

  const getTabFontSize = () => {
    return window.innerWidth > 1102 ? '16px' : '13px'
  }

  const tabs: EuiTabbedContentTab[] = [
    {
      id: 'executive-summary',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Executive Summary</div>
        </div>
      ),
      content: (
        <PageContent>
          <ExecutiveSummary masterCampaignPlan={masterCampaignPlan} onEditSectionClick={onEditSectionClick} ganttChartRef={ganttChartRef} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'market-analysis',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Analysis & Strategy</div>
        </div>
      ),
      content: (
        <PageContent>
          <MarketAnalysis masterCampaignPlan={masterCampaignPlan} onEditClick={onEditMarketAnalysisClick} printRef={marketAnalysisPrintRef} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'personas',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Personas</div>
        </div>
      ),
      content: (
        <PageContent>
          <Personas masterCampaignPlan={masterCampaignPlan} onEditPersonaClick={onEditPersonaClick} onAddPersonaClick={onAddPersonaClick} onReorderPersonasClick={onReorderPersonasClick} onDeletePersonaClick={onDeletePersonaClick} onDeletePersonaLoading={isRemovingPersona} isFetching={isMasterCampaignPlanFetching} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'media-plan',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Media Plan</div>,
      content: (
        <PageContent>
          <MediaPlan ref={mediaplanRef} masterCampaignPlan={masterCampaignPlan} onEditCampaignClick={onEditCampaignClick} onAddCampaignClick={onAddCampaignClick} onRemoveCampaignClick={onRemoveCampaignClick} isRemovingCampaign={isRemovingCampaign} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'projections',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Projections</div>,
      disabled: !masterCampaignPlan?.tvNetworkDistribution || !masterCampaignPlan?.internetMarginOfError || !masterCampaignPlan?.tvMarginOfError || !masterCampaignPlan?.billboardMarginOfError || !masterCampaignStub.campaignBuilders.some(cb => cb.cpm),
      content: (
        <PageContent>
          <Projections
            masterCampaignPlan={masterCampaignPlan}
            masterCampaignStub={masterCampaignStub}
            chartRefs={{
              deliveryMetricsRef,
              connectedTvRef,
              internetDisplayRef,
              targetedHouseholdsRef,
              billboardLocationsRef
            }}
            mapRefs={{
              targetedHouseholdMapRef,
              billboardMapRef
            }}
            onAllClientPdfsClick={onAllClientPdfsClick}
            onAllStaffPdfsClick={onAllStaffPdfsClick}
            staffPdfDisabledReason={staffPdfDisabledReason}
          />
        </PageContent>
      )
    },
    {
      id: 'client-preview-28daf838-c781-432b-b9bf-1d6994ff5d16',
      name: (
        <span className='euiFlexGroup euiFlexGroup--gutterSmall euiFlexGroup--alignItemsCenter euiFlexGroup--directionRow' style={{ margin: -2, fontSize: getTabFontSize() }}>
          <div className='euiFlexItem euiFlexItem--flexGrowZero' style={{ paddingLeft: '.5rem', paddingRight: '.5rem', margin: 2 }} onClick={onClientPreviewLinkClick}>
            Client Preview Link
          </div>
          <EuiButtonIcon
            className='euiFlexItem euiFlexItem--flexGrowZero'
            style={{ margin: 2 }}
            iconType='pencil'
            onClick={(e: any) => {
              e.stopPropagation()
              setIsClientPreviewTabsFlyoutOpen(true)
            }}
            aria-label='Edit Campaign Preview Tabs'
            size='s'
          />
        </span>
      ),
      content: (
        <PageContent>
          <EuiLoadingContent lines={10} />
        </PageContent>
      )
    }
  ]

  return (
    <React.Fragment>
      <Helmet>
        <title>Master Campaign Plan - {masterCampaignPlan.name}</title>
      </Helmet>

      <EuiPage className='euiPage--platform'>
        <EuiPageBody style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--euiHeaderHeightCompensation))' }}>
          <MasterCampaignPlanHeader planName={masterCampaignPlan.name} accountName={currentAccount?.name} onEditNameClick={onEditNameClick} />

          <EuiPanel>
            <EuiSpacer size='m' />

            <EuiTabbedContent tabs={tabs} selectedTab={tabs.find(tab => tab.id === selectedTab) || tabs[0]} onTabClick={tab => { dispatch(resetEditMode()); setSelectedTab(tab.id) }} className='master-campaign-plan-tabs' />
          </EuiPanel>
        </EuiPageBody>
      </EuiPage>

      {editFlyoutOpen && editFlyoutType && <EditFlyout editType={editFlyoutType} onClose={onEditFlyoutClose} />}

      {editCampaignFlyoutOpen && <EditCampaignFlyout editMode={editCampaignFlyoutOpen.mode} selectedCampaignBuilderId={editCampaignFlyoutOpen.selectedCampaignBuilderId} onClose={onEditCampaignFlyoutClose} />}

      {editPersonaFlyoutOpen && <EditPersonaFlyout editMode={editPersonaFlyoutOpen.mode} selectedPersona={editPersonaFlyoutOpen.selectedPersona} initialData={editPersonaFlyoutOpen.initialData} onClose={onEditPersonaFlyoutClose} />}

      {isReorderPersonasFlyoutOpen && <ReorderPersonasFlyout onClose={onReorderPersonasFlyoutClose} />}

      {isEditMarketAnalysisFlyoutOpen && <EditMarketAnalysisFlyout masterCampaignPlan={masterCampaignPlan} onClose={onEditMarketAnalysisFlyoutClose} />}

      {isClientPreviewTabsFlyoutOpen && (
        <ClientPreviewTabsFlyout
          currentSettings={{
            showExecutiveSummary: masterCampaignPlan.clientPreviewShowExecutiveSummary ?? true,
            showMarketAnalysis: masterCampaignPlan.clientPreviewShowMarketAnalysis ?? true,
            showPersonas: masterCampaignPlan.clientPreviewShowPersonas ?? true,
            showMediaPlan: masterCampaignPlan.clientPreviewShowMediaPlan ?? true,
            showProjections: masterCampaignPlan.clientPreviewShowProjections ?? true
          }}
          onSave={handleSaveClientPreviewTabs}
          onClose={() => setIsClientPreviewTabsFlyoutOpen(false)}
          isLoading={isUpdatingClientPreviewTabs}
        />
      )}

      {confirmTotalResetModalVisible && (
        <ConfirmTotalResetModal
          onClose={() => setConfirmTotalResetModalVisible(false)}
          isLoading={isResettingMasterCampaignPlan || isResettingMasterCampaignStub}
          onResetAll={async () => {
            if (!masterCampaignPlan?.id || !currentOrganization?.id || !currentAccount?.id || !masterCampaignStub?.id) {
              return
            }
            const resetPlanPromise = resetMasterCampaignPlanMutation({ organizationId: currentOrganization.id, accountId: currentAccount.id, masterCampaignPlanId: masterCampaignPlan.id })
            const resetStubPromise = resetMasterCampaignStubMutation({ masterCampaignPlanId: masterCampaignPlan.id, masterCampaignStubId: masterCampaignStub.id })

            await Promise.all([resetPlanPromise, resetStubPromise])

            setConfirmTotalResetModalVisible(false)
          }}
        />
      )}

      <MultiTabPdfProgressModal isStaffPdf={isStaffPdf} isVisible={showProgressModal} progress={pdfProgress} error={pdfError} onClose={onCloseProgressModal} onRetry={onRetryPdfGeneration} closeAutomatically />
    </React.Fragment>
  )
}

export default MasterCampaignPlanPage
